<template>
  <div class="app-page">
    <AppTopHeader />

    <!-- Hero -->
    <section class="hero">
      <div class="hero-glow" aria-hidden="true"></div>
      <ParticleField class="hero-particles" />
      <div class="hero-inner container">
        <div class="hero-text">
          <span class="badge">{{ $t('app.hero.badge') }}</span>
          <h1>{{ $t('app.hero.title') }}</h1>
          <p>{{ $t('app.hero.subtitle') }}</p>
          <div class="store-btns">
            <a
              class="store-btn store-btn--active"
              href="https://apps.apple.com/br/app/n%C3%B4made-digital/id6762011181"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$t('app.hero.appStoreAriaLabel')"
            >
              <svg class="store-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <span>
                <small>{{ $t('app.hero.appStoreSmall') }}</small>
                {{ $t('app.hero.appStoreName') }}
              </span>
            </a>
            <button class="store-btn" disabled :aria-label="$t('app.hero.googlePlayAriaLabel')">
              <svg class="store-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3.18 23.76c.37.2.8.2 1.19 0l11.5-6.64-2.5-2.5-10.19 9.14zM.5 1.42C.19 1.8 0 2.33 0 3v18c0 .67.19 1.2.5 1.58l.08.08L10.93 12.5v-.24L.58 1.34.5 1.42zM20.81 10.28l-2.85-1.64-2.83 2.83 2.83 2.83 2.88-1.66c.82-.47.82-1.89-.03-2.36zM4.37.24L15.87 6.88l-2.5 2.5L3.18.24C3.55.04 4 .04 4.37.24z"/>
              </svg>
              <span>
                <small>{{ $t('app.hero.googlePlaySmall') }}</small>
                {{ $t('app.hero.googlePlayName') }}
              </span>
            </button>
          </div>
          <p class="notify-label">{{ $t('app.hero.notifyLabel') }}</p>
          <form class="notify-form" @submit.prevent="submitNotify">
            <input
              v-model="email"
              type="email"
              :placeholder="$t('app.hero.notifyPlaceholder')"
              class="notify-input"
              :disabled="submitted || submitting"
              required
            />
            <button type="submit" class="notify-btn" :disabled="submitted || submitting">
              {{ submitted ? $t('app.hero.notifyBtnSubmitted') : submitting ? $t('app.hero.notifyBtnSubmitting') : $t('app.hero.notifyBtn') }}
            </button>
          </form>
          <p v-if="submitted" class="notify-success">{{ $t('app.hero.notifySuccess') }}</p>
          <p v-if="submitError" class="notify-error">{{ submitError }}</p>
        </div>
      </div>
      
      <!-- Scroll down indicator -->
      <div class="scroll-indicator" @click="scrollToContent" :aria-label="$t('app.hero.scrollAriaLabel')">
        <div class="mouse-icon">
          <div class="wheel"></div>
        </div>
        <div class="arrow-down"></div>
      </div>
    </section>

    <!-- Showcase — feature + print alternados -->
    <section class="showcase">
      <div
        v-for="(item, i) in showcase"
        :key="item.key"
        class="showcase-row reveal"
        :class="{ 'showcase-row--reverse': i % 2 === 1 }"
      >
        <div class="showcase-text">
          <span class="showcase-eyebrow">{{ item.eyebrow }}</span>
          <h2>{{ item.title }}</h2>
          <p>{{ item.desc }}</p>
          <ul class="showcase-list">
            <li v-for="(b, bi) in item.bullets" :key="bi">{{ b }}</li>
          </ul>
        </div>
        <div class="showcase-media">
          <div class="phone-frame">
            <img :src="item.img" :alt="item.title" class="phone-shot" loading="lazy" />
          </div>
          <div class="showcase-glow" aria-hidden="true"></div>
        </div>
      </div>
    </section>

    <!-- Features -->
    <section class="features container reveal">
      <h2>{{ $t('app.features.title') }}</h2>
      <p class="features-sub">{{ $t('app.features.subtitle') }}</p>
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon">✈️</div>
          <h3>{{ $t('app.features.trips.title') }}</h3>
          <p>{{ $t('app.features.trips.desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">💸</div>
          <h3>{{ $t('app.features.expenses.title') }}</h3>
          <p>{{ $t('app.features.expenses.desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">📸</div>
          <h3>{{ $t('app.features.timeline.title') }}</h3>
          <p>{{ $t('app.features.timeline.desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">🌍</div>
          <h3>{{ $t('app.features.feed.title') }}</h3>
          <p>{{ $t('app.features.feed.desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">🗺️</div>
          <h3>{{ $t('app.features.cityPages.title') }}</h3>
          <p>{{ $t('app.features.cityPages.desc') }}</p>
        </div>
        <div class="feature-card">
          <div class="feature-icon">⚡</div>
          <h3>{{ $t('app.features.offline.title') }}</h3>
          <p>{{ $t('app.features.offline.desc') }}</p>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="final-cta container">
      <h2>{{ $t('app.cta.title') }}</h2>
      <p>{{ $t('app.cta.subtitle') }}</p>
      <form class="notify-form notify-form--center" @submit.prevent="submitNotify">
        <input
          v-model="email"
          type="email"
          :placeholder="$t('app.cta.notifyPlaceholder')"
          class="notify-input"
          :disabled="submitted || submitting"
          required
        />
        <button type="submit" class="notify-btn" :disabled="submitted || submitting">
          {{ submitted ? $t('app.cta.notifyBtnSubmitted') : submitting ? $t('app.cta.notifyBtnSubmitting') : $t('app.cta.notifyBtn') }}
        </button>
      </form>
      <p v-if="submitted" class="notify-success notify-success--center">{{ $t('app.cta.notifySuccess') }}</p>
      <p v-if="submitError" class="notify-error notify-error--center">{{ submitError }}</p>
    </section>

    <footer class="app-footer">
      <div class="container footer-inner">
        <router-link to="/" class="footer-logo">{{ $t('app.footer.logo') }}</router-link>
        <div class="footer-links">
          <router-link to="/privacy">{{ $t('app.footer.privacy') }}</router-link>
          <router-link to="/terms">{{ $t('app.footer.terms') }}</router-link>
          <router-link to="/discover">{{ $t('app.footer.discover') }}</router-link>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import AppTopHeader from '../components/AppTopHeader.vue';
import ParticleField from '../components/ParticleField.vue';
import { flipmemoApiPath } from '@/config/flipmemoApi';

// Prints reais do app (otimizados) — src/assets/screens/
import shotTrips from '../assets/screens/trips.jpg';
import shotAi from '../assets/screens/ai.jpg';
import shotExpenses from '../assets/screens/expenses.jpg';
import shotTimeline from '../assets/screens/timeline.jpg';
import shotRoute from '../assets/screens/route.jpg';
import shotPassport from '../assets/screens/passport.jpg';
import shotShare from '../assets/screens/share.jpg';

export default {
  name: 'AppPage',
  components: { AppTopHeader, ParticleField },
  setup() {
    const { t, locale } = useI18n();
    useHead({
      title: computed(() => t('seo.home.title')),
      meta: [
        { name: 'description', content: computed(() => t('seo.home.description')) },
        { property: 'og:title', content: computed(() => t('seo.home.title')) },
        { property: 'og:description', content: computed(() => t('seo.home.description')) },
        { property: 'og:image', content: '/logo.svg' },
        { property: 'og:locale', content: computed(() => locale.value === 'pt-BR' ? 'pt_BR' : 'en_US') },
        { property: 'og:url', content: 'https://nomade-digital.com/' },
        { property: 'og:type', content: 'website' },
      ],
      link: [
        { rel: 'canonical', href: 'https://nomade-digital.com/' },
      ],
    });
  },
  data() {
    return {
      email: '',
      submitted: false,
      submitting: false,
      submitError: '',
    };
  },
  computed: {
    showcase() {
      const items = [
        { key: 'trips', img: shotTrips },
        { key: 'ai', img: shotAi },
        { key: 'expenses', img: shotExpenses },
        { key: 'timeline', img: shotTimeline },
        { key: 'route', img: shotRoute },
        { key: 'passport', img: shotPassport },
        { key: 'share', img: shotShare },
      ];
      return items.map((it) => ({
        ...it,
        eyebrow: this.$t(`app.showcase.${it.key}.eyebrow`),
        title: this.$t(`app.showcase.${it.key}.title`),
        desc: this.$t(`app.showcase.${it.key}.desc`),
        bullets: [1, 2, 3].map((n) => this.$t(`app.showcase.${it.key}.b${n}`)),
      }));
    },
  },
  mounted() {
    this.initReveal();
  },
  beforeUnmount() {
    if (this.revealObserver) this.revealObserver.disconnect();
  },
  methods: {
    async submitNotify() {
      if (!this.email || this.submitting) return;
      this.submitting = true;
      this.submitError = '';
      try {
        const res = await fetch(flipmemoApiPath('/waitlist'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email }),
        });
        if (res.ok || res.status === 201) {
          this.submitted = true;
        } else {
          const data = await res.json().catch(() => ({}));
          this.submitError = data.error || this.$t('app.errors.generic');
        }
      } catch {
        this.submitError = this.$t('app.errors.noConnection');
      } finally {
        this.submitting = false;
      }
    },
    initReveal() {
      const els = this.$el.querySelectorAll('.reveal');
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      this.revealObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
      );
      els.forEach((el) => this.revealObserver.observe(el));
    },
    scrollToContent() {
      const nextSection = this.$el.querySelector('.showcase');
      if (nextSection) {
        nextSection.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
};
</script>

<style scoped>
/* ── Base ── */
.app-page {
  background: #0d1b2e;
  color: #e2eaf4;
  font-family: Inter, 'Segoe UI', sans-serif;
  min-height: 100vh;
}
.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ── Hero ── */
.hero {
  position: relative;
  overflow: hidden;
  padding: 140px 0 120px;
  background: linear-gradient(135deg, #0d1b2e 0%, #0f2342 60%, #081d3a 100%);
}
.hero-glow {
  position: absolute;
  inset: -120px;
  background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(79, 142, 247, 0.15) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}
.hero-particles {
  z-index: 0;
}
.hero-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 32px;
}
.hero-text {
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.badge {
  display: inline-block;
  background: linear-gradient(90deg, #004ac6, #0ea5e9);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 5px 14px;
  margin-bottom: 20px;
}
.hero-text h1 {
  font-size: clamp(2rem, 4.5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
  margin: 0 0 18px;
  color: #e2eaf4;
  text-align: center;
}
.hero-text p {
  font-size: 18px;
  color: #94adc8;
  line-height: 1.65;
  margin: 0 auto 32px;
  max-width: 640px;
  text-align: center;
}
.store-btns {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 36px;
}
.store-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #0f172a;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 12px 20px;
  font-family: inherit;
  cursor: not-allowed;
  opacity: 0.65;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.15s;
}
.store-btn--active {
  cursor: pointer;
  opacity: 1;
}
.store-btn--active:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}
.store-btn span {
  display: flex;
  flex-direction: column;
  text-align: left;
  line-height: 1.3;
  font-size: 15px;
  font-weight: 700;
}
.store-btn small {
  font-size: 10px;
  font-weight: 400;
  opacity: 0.75;
}
.store-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}
.notify-label {
  font-size: 13px;
  font-weight: 600;
  color: #94adc8;
  margin: 0 auto 10px;
  text-align: center;
}
.notify-form {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}
.notify-form--center {
  justify-content: center;
}
.notify-input {
  flex: 1 1 220px;
  min-width: 0;
  border: 1.5px solid #1e3352;
  border-radius: 10px;
  padding: 11px 16px;
  font-size: 14px;
  font-family: inherit;
  background: #081526;
  color: #e2eaf4;
  outline: none;
  transition: border-color 0.2s;
}
.notify-input:focus {
  border-color: #4f8ef7;
}
.notify-btn {
  background: linear-gradient(135deg, #004ac6, #0284c7);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 11px 22px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  white-space: nowrap;
}
.notify-btn:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}
.notify-btn:disabled {
  opacity: 0.7;
  cursor: default;
}
.notify-success {
  margin: 10px auto 0;
  font-size: 14px;
  color: #34d399;
  font-weight: 600;
  text-align: center;
}
.notify-success--center {
  text-align: center;
}
.notify-error {
  margin: 10px auto 0;
  font-size: 13px;
  color: #f87171;
  font-weight: 600;
  text-align: center;
}
.notify-error--center {
  text-align: center;
}

/* ── Scroll Indicator ── */
.scroll-indicator {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  z-index: 10;
  transition: opacity 0.3s, transform 0.3s;
}
.scroll-indicator:hover {
  opacity: 0.85;
  transform: translateX(-50%) translateY(-2px);
}
.mouse-icon {
  width: 22px;
  height: 36px;
  border: 2px solid rgba(226, 234, 244, 0.45);
  border-radius: 12px;
  position: relative;
}
.mouse-icon .wheel {
  width: 4px;
  height: 8px;
  background-color: #cbd5e1;
  border-radius: 2px;
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  animation: scroll-wheel 1.6s infinite ease-in-out;
}
.arrow-down {
  width: 8px;
  height: 8px;
  border-right: 2px solid rgba(226, 234, 244, 0.45);
  border-bottom: 2px solid rgba(226, 234, 244, 0.45);
  transform: rotate(45deg);
  animation: scroll-arrow 1.6s infinite ease-in-out;
}

@keyframes scroll-wheel {
  0% {
    opacity: 0;
    top: 6px;
  }
  30% {
    opacity: 1;
  }
  80% {
    opacity: 0;
    top: 18px;
  }
  100% {
    opacity: 0;
    top: 6px;
  }
}

@keyframes scroll-arrow {
  0% {
    opacity: 0;
    transform: rotate(45deg) translate(-2px, -2px);
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: rotate(45deg) translate(2px, 2px);
  }
}

/* ── Features ── */
.features {
  padding: 96px 24px;
  background: #0d1b2e;
}
.features h2 {
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 800;
  text-align: center;
  letter-spacing: -0.02em;
  margin: 0 0 10px;
}
.features-sub {
  text-align: center;
  color: #94adc8;
  font-size: 17px;
  margin: 0 0 56px;
}
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}
.feature-card {
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 20px;
  padding: 32px 28px;
  transition: box-shadow 0.2s, transform 0.2s;
}
.feature-card:hover {
  box-shadow: 0 8px 32px rgba(79, 142, 247, 0.15);
  transform: translateY(-3px);
}
.feature-icon {
  font-size: 32px;
  margin-bottom: 16px;
}
.feature-card h3 {
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 10px;
  color: #e2eaf4;
}
.feature-card p {
  font-size: 14px;
  color: #94adc8;
  line-height: 1.65;
  margin: 0;
}

/* ── Showcase (feature + print) ── */
.showcase {
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 120px;
}
.showcase-row {
  display: flex;
  align-items: center;
  gap: 72px;
}
.showcase-row--reverse {
  flex-direction: row-reverse;
}
.showcase-text {
  flex: 1 1 0;
  min-width: 0;
}
.showcase-eyebrow {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #4f8ef7;
  margin-bottom: 14px;
}
.showcase-text h2 {
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0 0 16px;
  color: #e2eaf4;
}
.showcase-text > p {
  font-size: 17px;
  color: #94adc8;
  line-height: 1.65;
  margin: 0 0 24px;
  max-width: 460px;
}
.showcase-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.showcase-list li {
  position: relative;
  padding-left: 28px;
  font-size: 15px;
  color: #cbd9ea;
  line-height: 1.5;
}
.showcase-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, #004ac6, #0ea5e9);
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.12);
}
.showcase-list li::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 8px;
  width: 8px;
  height: 4px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg);
}
.showcase-media {
  flex: 0 0 auto;
  position: relative;
  display: flex;
  justify-content: center;
}
.phone-frame {
  position: relative;
  width: 260px;
  border-radius: 36px;
  padding: 10px;
  background: linear-gradient(160deg, #1c2f4d, #0c1626);
  border: 1px solid #24416b;
  box-shadow: 0 40px 90px rgba(0, 74, 198, 0.22), 0 10px 36px rgba(0, 0, 0, 0.35);
  z-index: 1;
  transition: transform 0.4s ease;
}
.showcase-row:hover .phone-frame {
  transform: translateY(-6px);
}
.phone-shot {
  width: 100%;
  height: auto;
  aspect-ratio: 1242 / 2688;
  border-radius: 28px;
  display: block;
}
.showcase-glow {
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  height: 120px;
  background: radial-gradient(ellipse, rgba(0, 74, 198, 0.28) 0%, transparent 70%);
  filter: blur(30px);
  pointer-events: none;
  z-index: 0;
}

/* ── Reveal on scroll ── */
.reveal {
  opacity: 0;
  transform: translateY(36px);
  transition: opacity 0.7s ease, transform 0.7s ease;
  will-change: opacity, transform;
}
.reveal.is-visible {
  opacity: 1;
  transform: none;
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ── Final CTA ── */
.final-cta {
  padding: 96px 24px;
  text-align: center;
}
.final-cta h2 {
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 14px;
}
.final-cta p {
  font-size: 17px;
  color: #94adc8;
  margin: 0 0 32px;
}

/* ── Footer ── */
.app-footer {
  background: #0f172a;
  padding: 32px 24px;
}
.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.footer-logo {
  color: #fff;
  font-weight: 800;
  font-size: 16px;
  text-decoration: none;
  letter-spacing: -0.02em;
}
.footer-links {
  display: flex;
  gap: 24px;
}
.footer-links a {
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  font-size: 13px;
  transition: color 0.15s;
}
.footer-links a:hover {
  color: #fff;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .hero {
    padding: 120px 0 100px;
  }
  .showcase {
    gap: 80px;
    padding-top: 64px;
  }
  .showcase-row,
  .showcase-row--reverse {
    flex-direction: column;
    gap: 36px;
    text-align: center;
  }
  .showcase-text > p {
    margin-left: auto;
    margin-right: auto;
  }
  .showcase-list {
    display: inline-flex;
    text-align: left;
  }
  .phone-frame {
    width: 220px;
  }
  .scroll-indicator {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    margin: 40px auto 0;
  }
  .scroll-indicator:hover {
    transform: translateY(-2px);
  }
}
@media (max-width: 480px) {
  .store-btns {
    flex-direction: column;
    align-items: center;
  }
}
</style>
